import { useCallback, useEffect, useState } from 'react';

interface UseDeviceOrientationReturn {
  /** True when viewport is narrow enough to be a phone/small tablet */
  isMobile: boolean;
  /** True when the device is in portrait orientation */
  isPortrait: boolean;
  /** Raw orientation type from screen.orientation API (if available) */
  orientationType: string | null;
}

const MOBILE_BREAKPOINT = 768;

function getIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT;
}

function getIsPortrait(): boolean {
  if (typeof screen !== 'undefined' && screen.orientation) {
    return screen.orientation.type.startsWith('portrait');
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(orientation: portrait)').matches;
  }
  return false;
}

function getOrientationType(): string | null {
  if (typeof screen !== 'undefined' && screen.orientation) {
    return screen.orientation.type;
  }
  return null;
}

export function useDeviceOrientation(): UseDeviceOrientationReturn {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [isPortrait, setIsPortrait] = useState(getIsPortrait);
  const [orientationType, setOrientationType] = useState(getOrientationType);

  const update = useCallback(() => {
    setIsMobile(getIsMobile());
    setIsPortrait(getIsPortrait());
    setOrientationType(getOrientationType());
  }, []);

  useEffect(() => {
    // screen.orientation change event (primary)
    const orientationHandler = () => update();
    if (screen.orientation) {
      screen.orientation.addEventListener('change', orientationHandler);
    }

    // matchMedia fallback for older browsers
    const mql = window.matchMedia?.('(orientation: portrait)');
    const mediaHandler = () => update();
    mql?.addEventListener?.('change', mediaHandler);

    // Resize for isMobile breakpoint tracking
    window.addEventListener('resize', update);

    return () => {
      screen.orientation?.removeEventListener('change', orientationHandler);
      mql?.removeEventListener?.('change', mediaHandler);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  return { isMobile, isPortrait, orientationType };
}
